// Brand-neutral lock chooser: one question at a time about the door, then the locks that
// fit it. Shared by /shop/find-my-lock, the shop home promo and the brand hubs (which
// filter the picks to their brand). The picks are not hand-written lists: every step
// filters the catalogue through lib/lockFit, so a lock can only ever land in the section
// its own data says it fits. lockChooser.test.js audits every path.
//
// The walk (Hugh, 17 Sep 2026, cut to three after normal people called six "way too
// much"): slides or swings → timber or aluminium → what hardware is on the door now → the
// picks. Nothing about shelter, swing direction or the backset: the weather rating is a
// spec on the product page, and the size comes from the door photos after payment, where
// every mortice lock is sold as the full lockset. Material does not narrow the picks (Hugh,
// 15 Sep: the door decides, not the label) — it only orders them, narrow-stile locks first
// for aluminium and the wide-profile ones first for timber.
//
// answers = { slides, material, onDoor, entry? }
//   entry — an optional results filter ('fingerprint' | 'phone'), not a step.
const { products } = require('../content/products');
const fitLib = require('./lockFit');

const STEPS = {
  slides: { q: 'Does the door slide or swing?', options: [['slides', 'Slides (ranch slider)'], ['swings', 'Swings on hinges']] },
  material: { q: 'What is the door made of?', options: [['aluminium', 'Aluminium'], ['timber', 'Timber']] },
  onDoor: {
    q: 'What is on the door now?',
    options: [
      ['bore', 'A round-bore deadbolt or lever (54 mm hole)'],
      ['gripset', 'An entrance gripset — long pull handle with a deadbolt above'],
      ['nightlatch', 'A night latch — box lock on the inside face'],
      ['mortice', 'A lock in the door edge with a lever or handle (mortice)'],
      ['euro', 'A lock with a euro or oval cylinder (the keyhole sits in a small plate)'],
      ['none', 'Nothing yet — a blank door'],
      ['other', 'More than one lock, or something else'],
    ],
  },
  onSlider: {
    q: 'What is on the door now?',
    key: 'onDoor',
    options: [
      ['hook', 'A hook lock with a snib lever'],
      ['pull', 'A flush pull with a small latch'],
      ['none', 'Nothing yet — a blank door'],
      ['other', 'Something else'],
    ],
  },
};

// "Our pick" order: the locks we stock and fit most for that door, then confirmed-supply
// brands, then price. The wide-profile 805W leads on a timber mortice door, the 905 on a stile.
const PREFERRED = ['e-lok-905', 'e-lok-915-sliding', 'e-lok-717-sliding', 'e-lok-805w', 'e-lok-505', 'e-lok-707', 'aqara-u100', 'aqara-u200'];
const PREFERRED_STANDARD = ['e-lok-805w', 'e-lok-1005', 'e-lok-905', 'aqara-u200'];
const CONFIRMED_SUPPLIERS = ['elements', 'konec'];
function rank(p, a) {
  // The wide-profile order only applies to a mortice-type timber door.
  const list = a.material === 'timber' && ['mortice', 'euro', 'none'].indexOf(a.onDoor) !== -1 ? PREFERRED_STANDARD : PREFERRED;
  const pref = list.indexOf(p.slug);
  const supplier = p.variants && p.variants[0] ? p.variants[0].supplier : '';
  return [pref === -1 ? list.length : pref, CONFIRMED_SUPPLIERS.indexOf(supplier) === -1 ? 1 : 0, p.priceIncGst || 0];
}
function byRank(a) {
  return (p, q) => {
    const rp = rank(p, a); const rq = rank(q, a);
    for (let i = 0; i < rp.length; i++) if (rp[i] !== rq[i]) return rp[i] - rq[i];
    return 0;
  };
}

// Every lock the chooser may ever recommend: a digital door lock we can sell, whose fit is known.
function universe() {
  return products.filter((p) => fitLib.isDigitalLock(p) && fitLib.isOrderable(p)
    && (fitLib.isSliding(p) || fitLib.isBoreSwap(p) || fitLib.isRetrofit(p) || (fitLib.isMortice(p) && fitLib.backsetKnown(p))));
}

// Does this lock fit the door the answers describe? (Only the answers given so far apply.)
function fitsDoor(p, a) {
  if (a.slides === 'slides') {
    if (!fitLib.isSliding(p)) return false;
  } else {
    if (a.slides !== 'swings') return false;
    if (fitLib.isSliding(p)) return false;
    if (a.onDoor && !fitLib.fitsOnDoor(p, a.onDoor)) return false;
  }
  return true;
}
// 'phone' means Apple Home / Google Home / Alexa; when no such lock fits the door, the locks
// with their own app stand in (remote unlock via the gateway), and the note says so.
function applyEntry(list, a) {
  if (a.entry === 'fingerprint') return { list: list.filter(fitLib.hasFingerprint), fallback: false };
  if (a.entry === 'phone') {
    const eco = list.filter(fitLib.hasEcosystem);
    return eco.length ? { list: eco, fallback: false } : { list: list.filter(fitLib.hasApp), fallback: true };
  }
  return { list: list, fallback: false };
}
// The locks still in play after the answers so far, best first.
function candidates(a) {
  const list = universe().filter((p) => fitsDoor(p, a));
  const e = applyEntry(list, a);
  return { list: e.list.sort(byRank(a)), fallback: e.fallback };
}

// Which steps to show, in order, given the answers so far.
function stepOrder(a) {
  const order = ['slides'];
  if (!a.slides) return order;
  order.push('material');
  order.push(a.slides === 'swings' ? 'onDoor' : 'onSlider');
  return order;
}

// The answer key a step writes (onSlider writes 'onDoor').
function keyOf(stepKey) {
  return STEPS[stepKey].key || stepKey;
}

// Done when every step the answers call for has an answer. "Something else" ends the walk on
// its own: no software pick, photos instead.
function isDone(a) {
  const order = stepOrder(a);
  return order.every((k) => a[keyOf(k)] !== undefined);
}

// One line that plays the customer's answers back, shown above the picks.
function describe(a) {
  const parts = [];
  parts.push((a.material === 'timber' ? 'timber' : a.material === 'aluminium' ? 'aluminium' : '') + (a.slides === 'slides' ? ' sliding door' : ' hinged door'));
  if (a.onDoor === 'bore') parts.push('round-bore deadbolt or lever');
  if (a.onDoor === 'gripset') parts.push('entrance gripset');
  if (a.onDoor === 'nightlatch') parts.push('night latch');
  if (a.onDoor === 'mortice') parts.push('mortice lock');
  if (a.onDoor === 'euro') parts.push('euro or oval cylinder');
  if (a.onDoor === 'hook') parts.push('hook lock');
  if (a.onDoor === 'pull') parts.push('flush pull and latch');
  if (a.onDoor === 'none') parts.push('no lock yet');
  if (a.onDoor === 'other') parts.push('more than one lock, or something unusual');
  const text = parts.join(', ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) + '.' : '';
}

// Returns { slugs: [...every lock that fits, best first], note: string|null }.
// The notes never ask for a photo before a sale: that ask comes after payment.
function recommend(a) {
  if (!isDone(a)) return { slugs: [], note: null };
  if (a.onDoor === 'other') {
    return {
      slugs: [],
      note: 'A digital lock replaces the main lock. Any extra deadbolt or night latch is disabled at install — unlocked, inside turn off — so it can never hold the door shut against a code, and nothing needs patching. Which lock suits depends on the main lock and the door, so this one is a photo job: a locksmith looks and picks it, no visit needed.',
    };
  }
  const c = candidates(a);
  const slugs = c.list.map((p) => p.slug);
  const notes = [];
  if (c.fallback) notes.push('No Apple Home, Google Home or Alexa lock fits this door yet; these give you app control from the lock’s own app, with remote unlock via the Wi-Fi gateway.');
  if (a.slides === 'slides') notes.push('Sliding-door locks come with the 28 or 35 mm sliding mortice, sized before we book.');
  if (a.onDoor === 'bore') notes.push('Each of these swaps straight onto the 54 mm round bore, no cutting.' + (slugs.indexOf('aqara-u300') !== -1 ? ' The U300 is the lever version for a covered entry.' : ''));
  if (a.onDoor === 'gripset') notes.push('The deadbolt above a gripset sits in a standard 54 mm bore, so these swap in; the long pull handle leaves holes below, which we hide with a cover plate.');
  if (a.onDoor === 'nightlatch') notes.push('A night latch usually has a 60 mm bore behind it, so a keypad deadbolt drops in; the old latch leaves marks inside that a cover plate hides.');
  if (a.onDoor === 'euro' && slugs.indexOf('aqara-u200') !== -1) notes.push('The U200 keeps your existing lock and keys; the others replace the cylinder lock as a set.');
  if (a.onDoor === 'mortice' && slugs.indexOf('aqara-u200') !== -1) notes.push('The U200 retrofits over the cylinder if your mortice lock has one; the others replace the lock as a set.');
  if (a.onDoor === 'none') notes.push('Blank door: with installation we cut it to the right backset on the day.');
  return { slugs, note: notes.length ? notes.join(' ') : null };
}

// Every completed answer set reachable from `answers` (a few dozen leaves).
function completions(answers) {
  const out = [];
  const walk = (a) => {
    if (isDone(a)) { out.push(a); return; }
    const order = stepOrder(a);
    let next = null;
    for (let i = 0; i < order.length; i++) if (a[keyOf(order[i])] === undefined) { next = order[i]; break; }
    if (!next) return;
    const opts = STEPS[next].options;
    for (let i = 0; i < opts.length; i++) {
      const b = Object.assign({}, a); b[keyOf(next)] = opts[i][0];
      walk(b);
    }
  };
  walk(answers || {});
  return out;
}

// Options for a step that can still end in a pick of `brand` (all options when no brand).
// brandOf(slug) → brand name; injected so the component decides how to look brands up.
function optionsFor(stepKey, answers, brand, brandOf) {
  const opts = STEPS[stepKey].options;
  if (!brand) return opts;
  return opts.filter((opt) => {
    const a = Object.assign({}, answers); a[keyOf(stepKey)] = opt[0];
    return completions(a).some((done) => recommend(done).slugs.some((slug) => brandOf(slug) === brand));
  });
}

module.exports = { STEPS, stepOrder, isDone, recommend, describe, completions, optionsFor, keyOf, universe, candidates, fitsDoor };
